import { FileBadge, FileDown, Plus, PackageCheck, Clock } from "lucide-react";
import { useCart } from "@/lib/cart";
import VialImage from "@/components/VialImage";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductCard({ p, onCoa }) {
  const { add } = useCart();
  return (
    <div className="group bg-white border border-slate-200 hover:border-sky-400 transition-colors flex flex-col" data-testid={`product-card-${p.id}`}>
      <div className="relative flex items-center justify-center bg-white border-b border-slate-100 pt-3 pb-1">
        <VialImage product={p} className="h-56 transition-transform duration-500 group-hover:-translate-y-1" />
        <span className="absolute bottom-2 right-3 font-tech text-[9px] uppercase tracking-[0.16em] text-slate-400" data-testid={`product-card-supplier-${p.id}`}>
          Mfr · Axis Bio Lab
        </span>
      </div>
      <div className="p-4 border-b border-slate-100 flex items-start justify-between gap-2">
        <div>
          <p className="font-tech text-[10px] uppercase tracking-[0.18em] text-slate-400">{p.sku}</p>
          <h3 className="font-display font-semibold text-lg text-slate-900 mt-1 leading-snug">{p.name}</h3>
          {p.alias && <p className="text-xs text-slate-500 mt-0.5">{p.alias}</p>}
          <p className="text-xs text-slate-500 mt-0.5">{p.specification}</p>
        </div>
        {p.has_coa && (
          <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-sm font-tech text-[10px] whitespace-nowrap">
            {p.coa_purity ? `${p.coa_purity}% HPLC` : p.reference_coa ? "Ref. COA" : "COA On File"}
          </Badge>
        )}
      </div>
      <div className="p-4 flex-1">
        <div className="flex items-center gap-1.5 font-tech text-[11px]">
          {p.turnaround
            ? <><Clock className="h-3.5 w-3.5 text-amber-600" /><span className="text-amber-700">5 business day turnaround</span></>
            : <><PackageCheck className="h-3.5 w-3.5 text-emerald-600" /><span className="text-emerald-700">In stock{p.stock ? ` · ${p.stock} units` : ""}</span></>}
        </div>
        {p.has_coa ? (
          <div className="mt-3 flex items-center gap-3">
            <button onClick={() => onCoa(p)} data-testid={`product-card-view-coa-${p.id}`}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-sky-700 hover:text-sky-900 transition-colors">
              <FileBadge className="h-3.5 w-3.5" /> Inspect COA
            </button>
            {p.has_coa_file && (
              <a href={`${API}/coa/${p.id}`} target="_blank" rel="noopener noreferrer" data-testid={`product-card-coa-pdf-${p.id}`}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 hover:text-slate-900 transition-colors">
                <FileDown className="h-3.5 w-3.5" /> PDF{p.coa_lot ? ` · ${p.coa_lot}` : ""}
              </a>
            )}
          </div>
        ) : p.reference_coa ? (
          <a href={`${API}/coa/${p.reference_coa.product_id}`} target="_blank" rel="noopener noreferrer" data-testid={`product-card-reference-coa-${p.id}`}
            className="mt-3 inline-flex items-start gap-1.5 text-xs text-slate-600 hover:text-slate-900 transition-colors leading-snug">
            <FileDown className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span>Reference COA · Lot {p.reference_coa.lot} ({p.reference_coa.specification.split(" ")[0]}) — batch-matched COA ships with order</span>
          </a>
        ) : (
          <p className="mt-3 text-xs text-slate-400">COA available on request</p>
        )}
      </div>
      <div className="p-4 border-t border-slate-100">
        <div className="flex items-end justify-between mb-3">
          <div>
            <p className="font-tech text-xl font-semibold text-slate-900">${p.retail_price.toFixed(2)}</p>
            <p className="font-tech text-[10px] text-slate-400 uppercase tracking-wide">per vial · 5+ ${p.wholesale_tier_5.toFixed(2)} · 10+ ${p.wholesale_tier_10.toFixed(2)}</p>
          </div>
        </div>
        <Button onClick={() => add(p)} data-testid={`product-card-add-btn-${p.id}`}
          className="w-full gap-1.5">
          <Plus className="h-4 w-4" /> Add to Order Request
        </Button>
      </div>
    </div>
  );
}
